<template>
    <article>
        <MetricDescription name="pg_up" type="gauge" class="metric">
            <template #description>
                Whether the Postgres server is reachable or not.
            </template>
            <template #source>
                The agent checks that a connection to the server is still alive on each scrape.
            </template>
        </MetricDescription>

        <MetricDescription name="pg_probe_seconds" type="gauge" class="metric">
            <template #description>
                How long it took to execute an empty SQL query (<var>";"</var>) on the server.
                This metric shows the round-trip time between the agent and the server.
            </template>
            <template #source>
                The time spent executing <var>db.Ping()</var>.
            </template>
        </MetricDescription>

        <MetricDescription name="pg_info" type="gauge" class="metric">
            <template #description>
                The server info.
            </template>
            <template #source>
                <a href="https://www.postgresql.org/docs/current/view-pg-settings.html" target="_blank" rel="noopener noreferrer">pg_settings</a>.server_version
            </template>
            <template #labels><var>server_version</var></template>
        </MetricDescription>

        <MetricDescription name="pg_setting" type="gauge" class="metric">
            <template #description>
                Value of the pg_setting variable.
            </template>
            <template #source>
                <div><a href="https://www.postgresql.org/docs/current/view-pg-settings.html" target="_blank" rel="noopener noreferrer">pg_settings</a></div>
                <div>The agent only collects variables of the following types: <var>integer</var>, <var>real</var>, and <var>bool</var>.</div>
            </template>
            <template #labels><var>name</var>, <var>unit</var></template>
        </MetricDescription>

        <MetricDescription name="pg_connections" type="gauge" class="metric">
            <template #description>
                The number of the database connections.
            </template>
            <template #source>
                <a href="https://www.postgresql.org/docs/current/monitoring-stats.html#MONITORING-PG-STAT-ACTIVITY-VIEW" target="_blank" rel="noopener noreferrer">pg_stat_activity</a>
            </template>
            <template #labels>
                <div><var>db</var></div>
                <div><var>user</var></div>
                <div><var>state</var> - current state of the connection, <var>&lt; active | idle | idle in transaction &gt;</var></div>
                <div>
                    <var>wait_event_type</var> - the <a href="https://www.postgresql.org/docs/current/monitoring-stats.html#WAIT-EVENT-TABLE" target="_blank" rel="noopener noreferrer">type</a>
                     of event that the connection is waiting for.
                </div>
                <div>
                    <var>query</var> - If the <var>state</var> of a connection is <var>active</var>, this is the currently executing query.
                    For <var>idle in transaction</var> connections, this is the last executed query. This label holds a normalized and obfuscated query.
                </div>
            </template>
        </MetricDescription>

        <MetricDescription name="pg_latency_seconds" type="gauge" class="metric">
            <template #description>
                Query execution time.
            </template>
            <template #source>
                <a href="https://www.postgresql.org/docs/current/monitoring-stats.html#MONITORING-PG-STAT-ACTIVITY-VIEW" target="_blank" rel="noopener noreferrer">pg_stat_activity</a>,
                <a href="https://www.postgresql.org/docs/current/pgstatstatements.html" target="_blank" rel="noopener noreferrer">pg_stat_statements</a>.
            </template>
            <template #labels>
                <var>summary</var> - <var>&lt; avg | max | p50 | p75 | p95 | p99 &gt;</var>
            </template>
        </MetricDescription>

        <MetricDescription name="pg_db_queries_per_second" type="gauge" class="metric">
            <template #description>
                Number of queries executed in the database.
            </template>
            <template #source>
                Aggregation of <var>pg_stat_activity.state = 'Active'</var> and <var>pg_stat_statements.calls</var>.
            </template>
            <template #labels>
                <var>db</var> - name of the database
            </template>
        </MetricDescription>

        <MetricDescription name="pg_lock_awaiting_queries" type="gauge" class="metric">
            <template #description>
                Number of queries awaiting a lock.
            </template>
            <template #source>
                Number of connections with <var>pg_stat_activity.wait_event_type = 'Lock'</var>.
                The <var>blocking_query</var> label is calculated using the <a href="https://www.postgresql.org/docs/current/functions-info.html" target="_blank" rel="noopener noreferrer">pg_blocking_pids</a> function.
            </template>
            <template #labels>
                <div><var>db</var></div>
                <div><var>user</var> </div>
                <div><var>blocking_query</var> - the query holding the lock</div>
            </template>
        </MetricDescription>

        <h2>Query metrics</h2>

        <p>
            The <a href="https://www.postgresql.org/docs/current/pgstatstatements.html" target="_blank" rel="noopener noreferrer">pg_stat_statements</a> view shows statistics only for queries that have been completed.
            So, to provide comprehensive statistics, the agent extends this with data about the currently active queries from
            the <a href="https://www.postgresql.org/docs/current/monitoring-stats.html#MONITORING-PG-STAT-ACTIVITY-VIEW" target="_blank" rel="noopener noreferrer">pg_stat_activity</a> view.
        </p>

        <p>
            Collecting stats about each query would produce metrics with very high cardinality.
            However, the primary purpose of such metrics is to show the most resource-consuming queries.
            So, the agent collects these metrics only for TOP-20 queries by total execution time.
        </p>

        <p>
            Each metric described below has <var>query</var>, <var>db</var> and <var>user</var> labels.
            <var>Query</var> is a normalized and obfuscated query from <var>pg_stat_statements.query</var>, and <var>pg_stat_activity.query</var>.
        </p>

        <p>
            For example, the following queries:
        </p>

        <pre v-hl.sql>
SELECT * FROM tbl WHERE id='1';
SELECT * FROM tbl WHERE id='2';
        </pre>

        <p>will be grouped to</p>

        <pre v-hl.sql>
SELECT * FROM tbl WHERE id=?;
        </pre>

        <MetricDescription name="pg_top_query_calls_per_second" type="gauge" class="metric">
            <template #description>
                Number of times the query has been executed.
            </template>
            <template #source>
                <var>pg_stat_statements.calls</var> and <var>pg_stat_activity.state = 'Active'</var>
            </template>
            <template #labels>
                <var>db</var>, <var>user</var>, <var>query</var>
            </template>
        </MetricDescription>

        <MetricDescription name="pg_top_query_time_per_second" type="gauge" class="metric">
            <template #description>
                Time spent executing the query.
            </template>
            <template #source>
                <var>clock_timestamp()-pg_stat_activity.query_start</var> and <var>pg_stat_statements.total_time</var>
            </template>
            <template #labels>
                <var>db</var>, <var>user</var>, <var>query</var>
            </template>
        </MetricDescription>

        <MetricDescription name="pg_top_query_io_time_per_second" type="gauge" class="metric">
            <template #description>
                Time the query spent awaiting I/O.
            </template>
            <template #source>
                <var>pg_stat_activity.wait_event_type = 'IO'</var>, <var>pg_stat_statements.blk_read_time</var> and <var>pg_stat_statements.blk_write_time</var>
            </template>
            <template #labels>
                <var>db</var>, <var>user</var>, <var>query</var>
            </template>
        </MetricDescription>


        <h2>Replication metrics</h2>

        <MetricDescription name="pg_wal_receiver_status" type="gauge" class="metric">
            <template #description>
                WAL receiver status: 1 if the receiver is connected, otherwise 0.
            </template>
            <template #source>
                <var>pg_stat_wal_receiver</var> and <var>pg_settings[primary_conninfo]</var>
            </template>
            <template #labels>
                <var>sender_host</var>, <var>sender_port</var>
            </template>
        </MetricDescription>

        <MetricDescription name="pg_wal_replay_paused" type="gauge" class="metric">
            <template #description>
                Whether WAL replay paused or not.
            </template>
            <template #source>
                <var>pg_is_wal_replay_paused()</var> or <var>pg_is_xlog_replay_paused()</var>
            </template>
        </MetricDescription>

        <MetricDescription name="pg_wal_current_lsn" type="counter" class="metric">
            <template #description>
                Current WAL sequence number.
            </template>
            <template #source>
                <var>pg_current_wal_lsn()</var> or <var>pg_current_xlog_location()</var>
            </template>
        </MetricDescription>

        <MetricDescription name="pg_wal_receive_lsn" type="counter" class="metric">
            <template #description>
                WAL sequence number that has been received and synced to disk by streaming replication.
            </template>
            <template #source>
                <var>pg_last_wal_receive_lsn()</var> or <var>pg_last_xlog_receive_location()</var>
            </template>
        </MetricDescription>

        <MetricDescription name="pg_wal_reply_lsn" type="counter" class="metric">
            <template #description>
                WAL sequence number that has been replayed during recovery.
            </template>
            <template #source>
                <var>pg_last_wal_replay_lsn()</var> or <var>pg_last_xlog_replay_location()</var>
            </template>
        </MetricDescription>

    </article>
</template>

<script>
import MetricDescription from "@/components/MetricDescription";

export default {
    components: {MetricDescription},
}
</script>

<style scoped>

.metric {
    margin: 36px 0;
}

</style>
